import { useEffect, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';

const WebGLCanvas = ({ zIndex = 999, className = '', activeEffects = [], effectClasses = {} }) => {
  const canvasRef = useRef(null);
  const appRef = useRef(null);
  const effectInstancesRef = useRef(new Map()); // Map of effect ID to effect instance

  const loadEffects = useCallback(() => {
    if (!appRef.current) return;
    
    // Get current effect IDs
    const currentEffectIds = new Set(activeEffects.map(e => e.id));
    const existingEffectIds = new Set(effectInstancesRef.current.keys());

    // Remove effects that no longer exist
    effectInstancesRef.current.forEach((effectInstance, effectId) => {
      if (!currentEffectIds.has(effectId)) {
        if (effectInstance.destroy) effectInstance.destroy();
        if (effectInstance.container && appRef.current.stage.children.includes(effectInstance.container)) {
          appRef.current.stage.removeChild(effectInstance.container);
        }
        effectInstancesRef.current.delete(effectId);
      }
    });

    // Add new effects
    activeEffects.forEach(effectData => {
      if (!existingEffectIds.has(effectData.id) && effectClasses[effectData.type]) {
        const EffectClass = effectClasses[effectData.type];
        const effectInstance = new EffectClass(appRef.current);
        effectInstance.effectId = effectData.id;
        effectInstance.effectData = effectData;
        
        if (effectInstance.container) {
          appRef.current.stage.addChild(effectInstance.container);
        }
        
        effectInstancesRef.current.set(effectData.id, effectInstance);
      }
    });

  }, [activeEffects, effectClasses]);

  useEffect(() => {
    if (!canvasRef.current) return;

    const initApp = async () => {
      const app = new PIXI.Application();
      
      await app.init({
        canvas: canvasRef.current,
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundAlpha: 0,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      appRef.current = app;

      const handleResize = () => {
        if (app && app.renderer) {
          app.renderer.resize(window.innerWidth, window.innerHeight);
        }
      };

      window.addEventListener('resize', handleResize);
      
      // Load effects after PIXI is fully initialized
      loadEffects();
    };

    initApp().catch(console.error);

    return () => {
      // Clean up effects first, but safely check if app still exists
      effectInstancesRef.current.forEach(effectInstance => {
        if (effectInstance.destroy && appRef.current) {
          try {
            effectInstance.destroy();
          } catch (e) {
            console.warn('Error destroying effect:', e);
          }
        }
      });
      effectInstancesRef.current.clear();

      // Clean up PIXI app
      if (appRef.current) {
        try {
          // Remove all event listeners
          window.removeEventListener('resize', () => {});
          
          // Stop ticker first and remove all listeners
          if (appRef.current.ticker) {
            appRef.current.ticker.stop();
            appRef.current.ticker.destroy();
          }
          
          // Clear stage
          if (appRef.current.stage) {
            appRef.current.stage.removeChildren();
          }
          
          // Destroy the app (this handles ticker, stage, renderer cleanup)
          appRef.current.destroy(true, {
            children: true,
            texture: true,
            baseTexture: true
          });
          appRef.current = null;          
        } catch (e) {
          console.warn('Error cleaning up PIXI app:', e);
        }
      }
    };
  }, []);

  // Handle effects changes
  useEffect(() => {
    if (!appRef.current) return;
    
    loadEffects();
  }, [activeEffects, loadEffects]);


  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: zIndex,
      }}
    />
  );
};

export default WebGLCanvas;